'use client';

import Image from 'next/image';
import { memo } from 'react';

export interface AdaptiveLogoProps {
  /** URL to the SVG logo */
  logoUrl?: string | undefined;
  /** Fallback text if no logo URL is provided */
  fallbackText?: string;
  /** Subtitle text shown below the logo */
  subtitle?: string;
  /** Color mode - 'light' for white logo, 'dark' for dark logo */
  colorMode?: 'light' | 'dark';
  /** Additional CSS classes */
  className?: string;
  /** Logo height in pixels (width auto-scales) */
  height?: number;
}

/**
 * Adaptive Logo Component for Stone Investment Theme
 * 
 * Renders an SVG logo that adapts its color based on the background:
 * - 'light' mode: White logo (for dark backgrounds) - uses CSS invert filter
 * - 'dark' mode: Original logo colors (for light backgrounds)
 * 
 * Uses CSS filter to adjust logo colors for different backgrounds.
 * Falls back to text display if no logo URL is provided.
 */
export const AdaptiveLogo = memo(function AdaptiveLogo({
  logoUrl,
  fallbackText = 'Stone Investment',
  subtitle,
  colorMode = 'dark',
  className = '',
  height = 40,
}: AdaptiveLogoProps) {
  const textColor = colorMode === 'light' ? 'text-white' : 'text-stone-900';

  if (!logoUrl) {
    // Fallback to text display
    return (
      <div className={`text-center ${textColor} ${className}`}>
        <span className="font-serif text-2xl md:text-3xl tracking-widest font-semibold">
          {fallbackText}
        </span>
        {subtitle && (
          <p className="hidden md:block text-xs uppercase tracking-[0.3em] mt-1 font-sans">
            {subtitle}
          </p>
        )}
      </div>
    );
  }

  // CSS filter for light mode (invert dark logo to white)
  // brightness(0) makes it black, then invert(1) makes it white
  const filterStyle = colorMode === 'light' 
    ? 'brightness(0) invert(1)' 
    : 'none';

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {/* SVG Logo with CSS filter for color adaptation */}
      <div
        className="relative transition-all duration-300 flex items-center justify-center"
        style={{
          height: `${height}px`,
          width: 'auto',
          maxWidth: '60vw',
        }}
      >
        <Image
          src={logoUrl}
          alt={fallbackText}
          height={height}
          width={height * 4}
          className="h-full w-auto object-contain transition-all duration-300"
          style={{
            filter: filterStyle,
            maxHeight: `${height}px`,
          }}
          priority
          fetchPriority="high"
          unoptimized
        />
      </div>
      {/* Optional subtitle */}
      {subtitle && (
        <p className={`hidden md:block text-xs uppercase tracking-[0.3em] mt-2 font-sans ${textColor}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
});
